<script setup lang="ts">
import { ref, watch } from 'vue'
import { useStore } from 'vuex'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toast'
import { apiErrorDetail } from '@/utils/roles'
import type { Kind, Role } from '@/utils/accessDomains'

export type RoleFormMode = 'create' | 'rename' | 'duplicate'

const props = defineProps<{
  open: boolean
  mode: RoleFormMode
  kind: Kind
  role?: Role | null
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'saved', roleId: number): void
}>()

const store = useStore()

const TITLES: Record<RoleFormMode, string> = {
  create: 'New role',
  rename: 'Rename role',
  duplicate: 'Duplicate role',
}

const form = ref({ name: '', description: '' })
const nameError = ref(false)
const submitting = ref(false)

watch(() => props.open, (open) => {
  if (!open) return
  nameError.value = false
  form.value = props.mode === 'create'
    ? { name: '', description: '' }
    : {
        name: props.mode === 'duplicate' ? `${props.role?.name ?? ''} copy` : props.role?.name ?? '',
        description: props.role?.description ?? '',
      }
})

const submit = async () => {
  const name = form.value.name.trim()
  if (!name) {
    nameError.value = true
    return
  }
  const description = form.value.description.trim()

  submitting.value = true
  try {
    const role: Role = props.mode === 'rename' && props.role
      ? await store.dispatch('access/updateRole', { roleId: props.role.role_id, name, description })
      : await store.dispatch('access/createRole', {
          kind: props.kind,
          name,
          description,
          copyFromRoleId: props.mode === 'duplicate' ? props.role?.role_id : undefined,
        })
    toast({ title: `${name} ${props.mode === 'rename' ? 'renamed' : 'created'}` })
    emit('update:open', false)
    emit('saved', role.role_id)
  } catch (e) {
    toast({ title: apiErrorDetail(e, "Couldn't save this role. Reload and try again."), variant: 'destructive' })
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="max-w-[400px]">
      <DialogHeader>
        <DialogTitle>{{ TITLES[mode] }}</DialogTitle>
        <DialogDescription v-if="mode === 'duplicate'">
          Starts with the current permissions of {{ role?.name }}.
        </DialogDescription>
        <DialogDescription v-else-if="mode === 'create'">
          Starts with no permissions.
        </DialogDescription>
        <DialogDescription v-else />
      </DialogHeader>

      <form class="space-y-3" @submit.prevent="submit">
        <div class="space-y-1.5">
          <Label for="role-name">Name</Label>
          <Input id="role-name" v-model="form.name" autofocus @input="nameError = false" />
          <p v-if="nameError" class="text-sm text-destructive">Name is required</p>
        </div>
        <div class="space-y-1.5">
          <Label for="role-description">Description</Label>
          <Input id="role-description" v-model="form.description" placeholder="Optional" />
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" @click="emit('update:open', false)">Cancel</Button>
          <Button type="submit" :disabled="submitting">{{ mode === 'rename' ? 'Save' : 'Create' }}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
